import { formatMoney } from '@theme/utilities';

/**
 * Web Component that manages subscription plan selection for a product
 * with variants structured as: 6 colors x 3 options (1 Jar sub, 3 Jars sub, One Time).
 *
 * Manages `input[name="id"]` (variant ID) and `input[name="selling_plan"]` (Seal selling plan ID)
 * so that `product-form.js` can read them on submit.
 */
class PlanSelector extends HTMLElement {
  #abortController;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // Parse variant data from the embedded JSON script
    this.variants = this.#parseVariantJson();
    if (!this.variants.length) return;

    // Cache DOM references
    // <plan-selector> is rendered inside <product-form>'s <form> as a child block,
    // so we can traverse up to find the form and its inputs directly.
    this.form = this.closest('form[action$="/cart/add"]');
    this.variantInput = this.form?.querySelector('input[name="id"]');
    // Ensure a selling_plan input exists inside the form
    this.sellingPlanInput = this.form?.querySelector('input[name="selling_plan"]');
    if (!this.sellingPlanInput && this.form) {
      this.sellingPlanInput = document.createElement('input');
      this.sellingPlanInput.type = 'hidden';
      this.sellingPlanInput.name = 'selling_plan';
      this.sellingPlanInput.value = '';
      this.form.appendChild(this.sellingPlanInput);
    }
    this.colorSwatches = this.querySelectorAll('[data-ref="color-swatches"] input[type="radio"]');
    this.colorDisplayEl = this.querySelector('[data-ref="color-display"]');
    this.planCards = this.querySelectorAll('[data-plan-card]');
    this.perJarLabel = this.dataset.perJarLabel || 'per jar';
    // One-time link lives outside the form (as a sibling block in the section), find it at section level
    const section = this.closest('.shopify-section') || this.closest('[data-ref="product-section-content"]') || document;
    this.oneTimeLink = section.querySelector('[data-ref="one-time-link"]');
    this.oneTimePriceEl = section.querySelector('[data-ref="one-time-price"]');

    // Bind event listeners with AbortController signal for automatic cleanup
    this.planCards.forEach((card) => {
      card.addEventListener('click', () => this.#onPlanCardClick(card), { signal });
    });

    this.colorSwatches.forEach((swatch) => {
      swatch.addEventListener('change', () => this.#onColorChange(), { signal });
    });

    if (this.oneTimeLink) {
      this.oneTimeLink.addEventListener('click', (e) => this.#onOneTimeLinkClick(e), { signal });
    }

    // Initialize: select the first plan card that is marked selected, or the first one
    this.#initDefaultSelection();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  // ---------------------------------------------------------------------------
  // Initialization
  // ---------------------------------------------------------------------------

  /**
   * Parses the variant JSON embedded in the component's script tag.
   * @returns {Array} Array of variant objects
   */
  #parseVariantJson() {
    const scriptEl = this.querySelector('script[data-variant-json]');
    if (!scriptEl) return [];

    try {
      return JSON.parse(scriptEl.textContent);
    } catch (e) {
      console.error('[plan-selector] Failed to parse variant JSON:', e);
      return [];
    }
  }

  /**
   * Selects the first plan card that already has the selected class,
   * or falls back to the first plan card. Triggers an initial variant update.
   */
  #initDefaultSelection() {
    const alreadySelected = this.querySelector('.plan-card--selected[data-plan-card]');
    const target = alreadySelected || this.planCards[0];

    if (target) {
      this.#selectPlanCard(target);
      this.#updateVariant();
    }
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  /**
   * Handles a click on a plan card. Selects the card and updates the variant.
   * @param {HTMLElement} card - The clicked plan card element
   */
  #onPlanCardClick(card) {
    if (card.classList.contains('plan-card--selected')) return;

    this.userInteracted = true;
    this.#selectPlanCard(card);
    this.#updateVariant();
  }

  /**
   * Handles color dropdown change. Updates the variant for the current plan
   * and refreshes the one-time price display.
   */
  #onColorChange() {
    this.userInteracted = true;
    // Update color display label
    if (this.colorDisplayEl) {
      this.colorDisplayEl.textContent = this.#selectedColor || '';
    }
    this.#updateVariant();
    this.#updateOneTimePrice();
  }

  /**
   * Handles click on the one-time purchase link.
   * Sets the one-time variant, clears selling plan, and submits the form.
   * @param {Event} e - The click event
   */
  #onOneTimeLinkClick(e) {
    e.preventDefault();

    const oneTimeVariant = this.#findOneTimeVariant();
    if (!oneTimeVariant) return;

    // Set the one-time variant and clear selling plan
    if (this.variantInput) {
      this.variantInput.value = oneTimeVariant.id;
    }
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = '';
    }

    // Submit the form directly — no need for VariantSelectedEvent
    if (this.form) {
      this.form.requestSubmit();
    }
  }

  // ---------------------------------------------------------------------------
  // Plan card selection
  // ---------------------------------------------------------------------------

  /**
   * Marks a plan card as selected and deselects all others.
   * @param {HTMLElement} card - The plan card to select
   */
  #selectPlanCard(card) {
    this.planCards.forEach((c) => c.classList.remove('plan-card--selected'));
    card.classList.add('plan-card--selected');

    // Notify free-gifts component about plan change
    this.dispatchEvent(new CustomEvent('plan-selector:plan-changed', {
      bubbles: true,
      detail: { planOption: card.getAttribute('data-plan-card') },
    }));
  }

  /**
   * Returns the currently selected plan card element.
   * @returns {HTMLElement|null}
   */
  get #selectedPlanCard() {
    return this.querySelector('.plan-card--selected[data-plan-card]');
  }

  // ---------------------------------------------------------------------------
  // Variant resolution
  // ---------------------------------------------------------------------------

  /**
   * Returns the currently selected color from the swatches.
   * @returns {string|null}
   */
  get #selectedColor() {
    const checked = this.querySelector('[data-ref="color-swatches"] input[type="radio"]:checked');
    return checked?.value || null;
  }

  /**
   * Returns the option2 value associated with the currently selected plan card.
   * The plan card stores the option2 text in its data-plan-card attribute.
   * @returns {string|null}
   */
  get #selectedPlanOption() {
    return this.#selectedPlanCard?.getAttribute('data-plan-card') || null;
  }

  /**
   * Finds the variant matching the current color and plan selection.
   * @returns {Object|null} The matching variant object, or null
   */
  #findCurrentVariant() {
    const color = this.#selectedColor;
    const planOption = this.#selectedPlanOption;

    if (!color || !planOption) return null;

    return this.variants.find(
      (v) => v.option1 === color && v.option2 === planOption
    ) || null;
  }

  /**
   * Finds the one-time purchase variant for the currently selected color.
   * @returns {Object|null} The matching one-time variant, or null
   */
  #findOneTimeVariant() {
    const color = this.#selectedColor;
    if (!color) return null;

    return this.variants.find(
      (v) => v.option1 === color && v.is_one_time === true
    ) || null;
  }

  // ---------------------------------------------------------------------------
  // Variant update
  // ---------------------------------------------------------------------------

  /**
   * Updates the hidden inputs (variant ID, selling plan) based on current selections,
   * dispatches the VariantSelectedEvent, and updates the browser URL.
   */
  #updateVariant() {
    const variant = this.#findCurrentVariant();
    if (!variant) return;

    // Update the hidden variant ID input
    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    // Update the selling plan input: set for subscriptions, clear for one-time
    if (this.sellingPlanInput) {
      if (!variant.is_one_time && variant.selling_plan_id) {
        // Extract the numeric ID from the GID (e.g., "gid://shopify/SellingPlan/123456" -> "123456")
        this.sellingPlanInput.value = this.#extractSellingPlanId(variant.selling_plan_id);
      } else {
        this.sellingPlanInput.value = '';
      }
    }

    // Update the ATC button price and re-enable it
    // We don't dispatch VariantSelectedEvent because that disables the ATC button
    // and expects a VariantUpdatedEvent with section rendering HTML to re-enable it.
    // Instead, we directly update the button's data attributes and re-enable it.
    const atcContainer = this.form?.querySelector('[data-ref="add-to-cart-button-container"]');
    if (atcContainer) {
      atcContainer.dataset.price = variant.price;
      atcContainer.dataset.compareAtPrice = variant.compare_at_price || '';
      const priceEl = atcContainer.querySelector('[data-ref="price"]');
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      const compareEl = atcContainer.querySelector('[data-ref="compare-at-price"]');
      if (compareEl) {
        compareEl.textContent = variant.compare_at_price && variant.compare_at_price > variant.price
          ? formatMoney(variant.compare_at_price) : '';
      }
    }
    const atcButton = this.form?.querySelector('[data-ref="add-to-cart-button"]');
    if (atcButton) {
      atcButton.disabled = !variant.available;
    }

    // Update plan card prices for the current color (all cards)
    this.#updatePlanCardPrices();

    // Update the URL with ?variant=ID on product pages
    this.#updateUrl(variant.id);
  }

  /**
   * Updates prices on all plan cards for the currently selected color.
   * Also recomputes unit prices (price / jar count).
   */
  #updatePlanCardPrices() {
    const color = this.#selectedColor;
    if (!color) return;

    this.planCards.forEach(card => {
      const planOption = card.getAttribute('data-plan-card');
      const jarCount = parseInt(card.dataset.jarCount) || 1;
      const variant = this.variants.find(v => v.option1 === color && v.option2 === planOption);
      if (!variant) return;

      const unitPrice = Math.round(variant.price / jarCount);
      const oneTimeVariant = this.variants.find(v => v.option1 === color && v.is_one_time);
      const priceEl = card.querySelector('[data-ref="unit-price"]');
      const compareEl = card.querySelector('[data-ref="unit-compare-price"]');

      if (priceEl) priceEl.textContent = formatMoney(unitPrice);
      if (compareEl) {
        if (oneTimeVariant && oneTimeVariant.price > unitPrice) {
          compareEl.textContent = formatMoney(oneTimeVariant.price);
          compareEl.style.display = '';
        } else {
          compareEl.textContent = '';
          compareEl.style.display = 'none';
        }
      }
    });
  }

  /**
   * Extracts the numeric selling plan ID from a Shopify GID string.
   * If the value is already numeric, returns it as-is.
   * @param {string} gid - e.g. "gid://shopify/SellingPlan/123456"
   * @returns {string} The numeric ID, e.g. "123456"
   */
  #extractSellingPlanId(gid) {
    if (!gid || gid === 'null') return '';

    const str = String(gid);

    // If it's a GID format, extract the last segment
    if (str.startsWith('gid://')) {
      const parts = str.split('/');
      return parts[parts.length - 1] || '';
    }

    // Already a plain ID
    return str;
  }

  /**
   * Updates the browser URL with the variant query parameter.
   * Only applies on product pages (not in quick-add dialogs or cards).
   * Skips the update on initial page load when the URL is clean — only
   * rewrites if `?variant=` is already present (shared link) or the user
   * has clicked a plan card (tracked via `this.userInteracted`).
   * @param {number|string} variantId - The variant ID to set
   */
  #updateUrl(variantId) {
    if (typeof Theme === 'undefined') return;
    if (Theme.template?.name !== 'product') return;

    const url = new URL(window.location.href);
    const hasVariantParam = url.searchParams.has('variant');

    if (!hasVariantParam && !this.userInteracted) return;

    url.searchParams.set('variant', variantId);

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  // ---------------------------------------------------------------------------
  // One-time price display
  // ---------------------------------------------------------------------------

  /**
   * Updates the one-time price element to reflect the current color's one-time variant price.
   */
  #updateOneTimePrice() {
    if (!this.oneTimePriceEl) return;

    const oneTimeVariant = this.#findOneTimeVariant();
    if (!oneTimeVariant) return;

    this.oneTimePriceEl.textContent = formatMoney(oneTimeVariant.price);
  }
}

if (!customElements.get('plan-selector')) {
  customElements.define('plan-selector', PlanSelector);
}
